import {AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, input, numberAttribute, OnDestroy, signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {MatButton, MatIconButton} from "@angular/material/button";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {BildeVelgerKomponent} from "web-ng2/delt/komponenter/skjema/bilde-velger/bilde-velger";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {Base64DataUrl} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import Croppie from "croppie";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {debounceTime, filter, fromEvent, switchMap} from "rxjs";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";


const debug = initDebugger('BildeVelgerMedSkaleringKomponent');

/**
 * Skjema for å velge bilde og skalere dette
 */
@Component({
    selector: 'bilde-velger-med-skalering',
    templateUrl: 'bilde-velger-med-skalering.html',
    styleUrl: 'bilde-velger-med-skalering.scss',
    host: {
        '[class.bilde-valgt]': 'erBildeValgt()',
        '[style.--croppieKontainerDimensjon]': 'croppieKontainerDimensjon()+"px"'
    },
    imports: [
        BildeVelgerKomponent,
        MatButton,
        TranslateModule,
        MatIconButton,
        MatIcon,
        MatTooltip,
        KlikkMedStopPropagationDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BildeVelgerMedSkaleringKomponent implements AfterViewInit, OnDestroy {

    skjemakontroll = input.required<FormControl<Base64DataUrl | null>>();
    knappetekst = input<string>(this.oversettelser.oversett(i18n('Endre bilde')));
    /** Størrelsen på bildet (i piksler) som vises i croppie (størrelse på ringen som markerer valgt utsnitt) )*/
    bildedimensjon = input(120, {transform: numberAttribute});

    valgtBildeSkjema = new FormControl<Base64DataUrl | null>(null);

    /** Sann så lenge bruker er i prosessen med å velge et bilde via bilde-velger-komponenten */
    velgBildeAktiv = signal(false);
    /** Sann dersom bruker har valgt et bilde via bilde-velger-komponenten */
    erBildeValgt = signal(false);
    readonly maksAvatarStoerrelse: number;
    /** bredde/høyde til croppie kontainer som bilde vises i */
    readonly croppieKontainerDimensjon = computed(() => this.bildedimensjon() + 40);

    private roterBildeKnapp = viewChild.required('roterBildeKnapp', {read: ElementRef<HTMLElement>});
    private croppieKontainer = viewChild.required<ElementRef<HTMLElement>>('croppieKontainer');
    private croppie!: Croppie; //blir initialisert i ngOnInit

    constructor(konfigurasjon: Appkonfigurasjon, private oversettelser: Oversettelser, private destroyRef: DestroyRef) {
        this.maksAvatarStoerrelse = konfigurasjon.maksAvatarStørrelse;
        this.valgtBildeSkjema.valueChanges.pipe(
          filter(bilde => erIkkeNil(bilde)),
          //aktiver croppie på valgt bilde
          switchMap(bilde => this.lastBildeICroppie(bilde)),
          takeUntilDestroyed(this.destroyRef))
          .subscribe(bilde => {
              //sørger for å propagere valg bilde til skjemakontroll angitt av forelder
              //propagering av bilde manipulert vhja croppie håndteres via this.initCroppie
              this.skjemakontroll().setValue(bilde);
          });
    }

    /** kalles når bildevelger åpnes (fil-velger-dialog vises) */
    bildevelgerAktivert() {
        this.velgBildeAktiv.set(true);
    }

    /** kalles når bruker har valgt en fil (fil-velger-dialog lukket).
     * bildefil er null dersom bruker ikke valgte noen fil. */
    bildeValgt(bildefil: File | null) {
        this.velgBildeAktiv.set(false);
        this.erBildeValgt.set(erIkkeNil(bildefil));
    }

    roter() {
        this.croppie.rotate(90);
    }

    tilbakestill() {
        this.erBildeValgt.set(false);
        this.skjemakontroll().setValue(null);
    }

    ngAfterViewInit(): void {
        this.initCroppie();
    }

    ngOnDestroy(): void {
        this.croppie?.destroy();
    }

    //private hjelpemetoder

    private initCroppie() {
        const croppieKontainer = this.croppieKontainer().nativeElement;
        this.croppie = new Croppie(croppieKontainer, {
            enableExif: true,
            enableOrientation: true, //kreves av croppie.rotate(..)
            //størrelse på 'ringen' som markerer utsnittet av bildet
            viewport: {
                width: this.bildedimensjon(),
                height: this.bildedimensjon(),
                type: 'circle'
            },
            //maksimal størrelse på bildet
            boundary: {
                width: this.croppieKontainerDimensjon(),
                height: this.croppieKontainerDimensjon()
            }
        });

        //sørg for å levere manipulert bilde til skjemakontroll
        fromEvent(croppieKontainer, 'update')
          .pipe(
            // logVerdiLevert('BildeVelgerMedSkaleringKomponent oppdatert PRE'),
            debounceTime(1000),
            switchMap(() => this.croppie.result({type: 'base64', size: 'viewport', circle:false})),
            //logVerdiLevert('BildeVelgerMedSkaleringKomponent oppdatert post'),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(bildeBase64 => this.skjemakontroll().setValue(bildeBase64));

        //flytter roter-knappen inn i dom generert av croppie slik at knappen vises ved siden av skalerings-slideren til croppie
        croppieKontainer.querySelector('.cr-slider-wrap')!.appendChild(this.roterBildeKnapp().nativeElement);
    }

    private async lastBildeICroppie(bilde: Base64DataUrl): Promise<Base64DataUrl> {
        await this.croppie.bind({url: bilde});
        return bilde;
    }
}
